import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MoreHorizontal, Pencil, Plus, Trash2 } from "lucide-react";

import {
  Button, ConfirmDialog, EmptyState, ErrorState, IconButton, Menu, MenuContent, MenuItem, MenuTrigger, Notice,
  PageHeader, Select, SkeletonRows, Surface, cn, focusRing, plainError,
} from "../../../components/ui";
import {
  CalendarAgenda, CalendarDayDetail, CalendarToolbar, fullDate, longDay, monthAnchorId, monthName, monthOf, yearOf,
} from "../../../components/calendar";
import { businessApi } from "../../../services/api";
import ClosureDayDialog from "./ClosureDayDialog";
import { CLOSURE_KIND, CLOSURE_ORDINARY, MAINTAINED_BY, calendarEntries, officialDetail } from "./calendarView";

// Administration › Calendar (hr-revamp/39 §K–§M, HC-D8). One chronological list of the year: the official holidays
// Apparify maintains from government proclamations (read only, each with its source) and this business's own closure
// days (which it may add, and change or remove until the date arrives). Payroll, attendance and leave read this
// calendar. Nothing here sets a pay rate.

const SHOW = [["all", "All days"], ["official", "Official holidays"], ["closures", "Closure days"]];
const ANCHOR = "calendar-month";

const link = cn("rounded-control font-medium text-accent hover:underline", focusRing);

export default function AdministrationCalendar() {
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [state, setState] = useState({ loading: true, error: "", restricted: false, data: null });
  const [show, setShow] = useState("all");
  const [notice, setNotice] = useState(null);
  const [open, setOpen] = useState(null); // the agenda entry whose detail is shown
  const [editing, setEditing] = useState(null); // "new" | closure
  const [removing, setRemoving] = useState(null);
  const years = useRef(new Map()); // year → loaded calendar, for the closure form's date check

  const load = useCallback(async (target, { quiet = false } = {}) => {
    if (!quiet) setState((current) => ({ ...current, loading: true, error: "" }));
    try {
      const response = await businessApi.getCalendarYear(target);
      const data = response?.calendar || null;
      if (data) years.current.set(Number(target), data);
      setState({ loading: false, error: "", restricted: false, data });
    } catch (err) {
      if (err?.status === 403) setState({ loading: false, error: "", restricted: true, data: null });
      else setState({ loading: false, error: plainError(err, "Check your connection and try again."), restricted: false, data: null });
    }
  }, []);

  useEffect(() => { load(year); }, [load, year]);

  const data = state.data && Number(state.data.year) === Number(year) ? state.data : null;
  const published = data?.publishedYears || [];
  const canGo = (target) => published.includes(target);
  const today = data?.today || "";
  const lastDate = published.length ? `${Math.max(...published)}-12-31` : "";
  const canManage = Boolean(data?.canManageClosures);

  const entries = useMemo(() => calendarEntries(data, { show }), [data, show]);
  const months = useMemo(() => [...new Set(entries.map((entry) => monthOf(entry.date)))], [entries]);

  // The official observances of any date, loading that date's year once if the page has not shown it yet.
  const officialOn = useCallback(async (date) => {
    const target = yearOf(date);
    let known = years.current.get(target);
    if (!known) {
      const response = await businessApi.getCalendarYear(target);
      known = response?.calendar || null;
      if (known) years.current.set(target, known);
    }
    return (known?.officialDays || []).filter((day) => day.date === date);
  }, []);

  const jumpTo = (month) => {
    const heading = document.getElementById(monthAnchorId(ANCHOR, month));
    if (!heading) return;
    heading.scrollIntoView({ block: "start" });
    heading.focus({ preventScroll: true });
  };

  const afterSave = async (saved, wasEditing) => {
    setEditing(null);
    setOpen(null);
    const savedYear = saved?.date ? yearOf(saved.date) : year;
    years.current.delete(savedYear);
    setNotice({ tone: "success", text: `${wasEditing ? "Closure day changed" : "Closure day added"}: ${saved?.name || "Closure"} on ${fullDate(saved?.date)}.` });
    if (savedYear !== year && canGo(savedYear)) setYear(savedYear);
    else await load(year, { quiet: true });
  };

  const remove = async () => {
    const closure = removing;
    try {
      await businessApi.removeClosureDay(closure.id, closure.updatedAt);
    } catch (err) {
      // Someone already removed it: the result the person wanted is in place.
      if (err?.status !== 404) throw err;
    }
    setRemoving(null);
    setOpen(null);
    setNotice({ tone: "success", text: `Closure day removed: ${closure.name} on ${fullDate(closure.date)}.` });
    await load(year, { quiet: true });
  };

  const closureActions = (closure) => (closure.canChange ? (
    <Menu>
      <MenuTrigger asChild>
        <IconButton icon={MoreHorizontal} label={`Actions for ${closure.name}`} size="sm" />
      </MenuTrigger>
      <MenuContent>
        <MenuItem icon={Pencil} onSelect={() => setEditing(closure)}>Change</MenuItem>
        <MenuItem icon={Trash2} tone="danger" onSelect={() => setRemoving(closure)}>Remove</MenuItem>
      </MenuContent>
    </Menu>
  ) : null);

  const agenda = entries.map((entry) => (entry.type === "closure" ? { ...entry, actions: closureActions(entry.closure) } : entry));

  const detail = (() => {
    if (!open) return null;
    if (open.type === "closure") {
      // Read the closure from the current data so the detail never shows a stale copy.
      const closure = (data?.closures || []).find((row) => row.id === open.closure.id) || open.closure;
      return {
        title: closure.name,
        subtitle: `${fullDate(closure.date)} · ${CLOSURE_KIND}`,
        sections: [
          { facts: [{ label: "Date", value: fullDate(closure.date) }, { label: "Kind", value: "Closure day, set by your business" }, ...(closure.note ? [{ label: "Note", value: closure.note }] : [])] },
          { title: "What this means", body: <><p>{CLOSURE_ORDINARY}</p><p>A closure day is not an official holiday and adds no holiday pay.</p></> },
        ],
        footnote: closure.canChange ? null : canManage ? "This closure day has arrived, so it is kept as a record and can no longer be changed or removed." : null,
        actions: closure.canChange ? (
          <>
            <Button onClick={() => { setOpen(null); setRemoving(closure); }}>Remove</Button>
            <Button variant="primary" onClick={() => { setOpen(null); setEditing(closure); }}>Change</Button>
          </>
        ) : null,
      };
    }
    const official = officialDetail(data, open.date);
    if (!official) return null;
    const value = (fact) => (fact.link
      ? <a href={fact.link.href} target="_blank" rel="noopener noreferrer" className={link}>{fact.link.text}<span className="sr-only"> (opens in a new tab)</span></a>
      : fact.value);
    return {
      title: official.title,
      subtitle: official.subtitle,
      sections: [
        ...official.groups.map((group) => ({ title: official.several ? group.name : undefined, facts: group.facts.map((fact) => ({ label: fact.label, value: value(fact) })) })),
        { title: "What payroll does", body: <p>{official.payroll}</p> },
        ...(official.limits ? [{ title: "What a person checks", body: <p>{official.limits}</p> }] : []),
      ],
      footnote: MAINTAINED_BY,
      actions: null,
    };
  })();

  const header = (
    <PageHeader
      title="Calendar"
      description="Official holidays come from government proclamations. Closure days are set by your business."
      actions={canManage ? <Button variant="primary" icon={Plus} className="max-sm:w-full" onClick={() => setEditing("new")}>Add closure day</Button> : null}
    />
  );

  if (state.restricted) {
    return (
      <div className="space-y-6">
        {header}
        <Surface><EmptyState title="The calendar isn't part of your role" description="Ask the business owner or an HR manager if you need to see official holidays and closure days." /></Surface>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {header}
      {notice ? <Notice tone={notice.tone} role="status">{notice.text}</Notice> : null}

      <Surface
        toolbar={(
          <CalendarToolbar
            label={String(year)}
            periodName="year"
            canPrevious={canGo(year - 1)}
            canNext={canGo(year + 1)}
            onPrevious={() => setYear(year - 1)}
            onNext={() => setYear(year + 1)}
          >
            <Select aria-label="Jump to a month" value="" disabled={!months.length} onChange={(event) => { if (event.target.value) jumpTo(event.target.value); }} className="h-control-sm text-control-sm">
              <option value="">Jump to month</option>
              {months.map((month) => <option key={month} value={month}>{monthName(month)}</option>)}
            </Select>
            <Select aria-label="Show" value={show} onChange={(event) => setShow(event.target.value)} className="h-control-sm text-control-sm">
              {SHOW.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </Select>
          </CalendarToolbar>
        )}
      >
        {state.loading && !data ? (
          <SkeletonRows rows={8} columns={4} label="Loading the calendar" />
        ) : state.error && !data ? (
          <ErrorState title="The calendar couldn't be loaded" description={state.error} onRetry={() => load(year)} />
        ) : (
          <>
            {data && !data.basePublished ? (
              <div className="p-4">
                <Notice>
                  The {year} official calendar has not been published in Apparify yet. Payroll for {year} dates waits for it.
                </Notice>
              </div>
            ) : null}
            {agenda.length ? (
              <CalendarAgenda entries={agenda} today={today} idPrefix={ANCHOR} label={`${year} calendar`} onOpen={setOpen} />
            ) : data?.basePublished ? (
              <EmptyState
                title={show === "closures" ? `No closure days in ${year}` : `Nothing to show for ${year}`}
                description={show === "closures" ? "A closure day is a day your business does not operate, such as a stock count." : undefined}
                action={show === "closures" && canManage ? <Button size="sm" onClick={() => setEditing("new")}>Add closure day</Button> : null}
              />
            ) : null}
          </>
        )}
      </Surface>

      {data ? (
        <p className="text-meta text-secondary">
          Nationwide days only: local holidays of a city or province are not included.{data.note ? ` ${data.note}` : ""}
        </p>
      ) : null}

      <CalendarDayDetail
        open={Boolean(detail)}
        onClose={() => setOpen(null)}
        title={detail?.title || ""}
        subtitle={detail?.subtitle}
        sections={detail?.sections}
        footnote={detail?.footnote}
        actions={detail?.actions}
      />

      {editing ? (
        <ClosureDayDialog
          closure={editing === "new" ? null : editing}
          today={today}
          lastDate={lastDate}
          officialOn={officialOn}
          onClose={(stale) => { setEditing(null); if (stale) { setOpen(null); load(year, { quiet: true }); } }}
          onSaved={afterSave}
        />
      ) : null}

      {removing ? (
        <ConfirmDialog
          title={`Remove the closure on ${longDay(removing.date)}?`}
          facts={[{ label: "Date", value: fullDate(removing.date) }, { label: "Name", value: removing.name }]}
          consequence="Employees scheduled that day will be expected at work. Payroll already calculated for that date will need to be recalculated."
          tone="danger"
          confirmLabel="Remove closure"
          cancelLabel="Keep closure"
          failureTitle="Not removed"
          errorFallback="The closure day was not removed. Try again."
          onConfirm={remove}
          onClose={() => setRemoving(null)}
        />
      ) : null}
    </div>
  );
}
